import classNames from 'classnames';
import { useSelector } from 'react-redux';

import useGoToCheckout from '../../../hooks/useGoToCheckout';
import { getPurchasesBannerData } from '../../../store/slices/catalogSlice';
import { getPaymentsState, getProductForUserSegment, getTrialPlan } from '../../../store/slices/paymentsSlice';
import { gaEvents } from '../../../utils/analytics';
import Spinner from '../Spinner';
import styles from './styles.module.scss';

export const paymentBtnType = {
    trial: 'trial',
    subscription: 'subscription',
    product: 'product'
};

/**
 * `plan` overrides the catalogue lookup below. Callers that let the customer
 * choose between plans — the out-of-messages paywall — must pass the one they
 * picked, otherwise the selector's `find` silently buys a different plan.
 *
 * `onClick`, when supplied, replaces the default straight-to-checkout
 * behavior — called with the resolved plan/product id instead of navigating.
 * HORO-10495: this is what lets TarotLimitModal reuse this exact button
 * (styling, loading state, GA event handling) for its anonymous-user path,
 * which can't go straight to checkout (a protected route) and needs to open
 * the sign-up modal first instead.
 *
 * `source` (HORO-10635) names the upsell surface for callers that go straight
 * to checkout without a payment modal (see SUBSCRIPTION_SOURCES in const.js);
 * inside the modal it is left unset and useGoToCheckout falls back to the
 * source the modal was opened with.
 *
 * HORO-10554: a caller that shows no particular plan passes nothing, and
 * checkout asks. There is deliberately no fallback here. It used to read
 * `premium?.id` from a selector that returned the first non-trial plan in the
 * catalogue, so a button offering no choice still charged one.
 */
const PaymentButton = ({
    text = '',
    disable = false,
    type = 'product',
    extraClasses = [],
    abTesting = false,
    googleEvent = '',
    plan = null,
    onClick = null,
    source = null
}) => {
    const goToCheckout = useGoToCheckout();
    const { title } = useSelector(getPurchasesBannerData);
    const { loading } = useSelector(getPaymentsState);
    const trial = useSelector(getTrialPlan);
    const product = useSelector(getProductForUserSegment);

    let paymentData = { paymentType: type, ...(source ? { source } : {}) };
    switch (type) {
        case 'product':
            paymentData = {
                ...paymentData,
                paymentId: product?.id,
                paymentPrice: product?.price
            };
            break;
        case 'subscription':
            paymentData = {
                ...paymentData,
                paymentId: plan?.id,
                paymentPrice: plan?.price
            };
            break;
        case 'trial':
            paymentData = {
                ...paymentData,
                paymentId: trial?.id,
                paymentPrice: trial?.price
            };
            break;
    }

    const onPaymentBtn = () => {
        if (abTesting && type === 'trial') {
            gaEvents.onTrialBannerClick(title);
        }

        switch (googleEvent) {
            case 'chatPopup':
                gaEvents.onPaymentPopupBtn(type);
                break;
            case 'profile':
                gaEvents.onPaymentProfileBtn(type);
                break;
            case 'issue':
                gaEvents.onIssueBtn();
                break;
        }

        if (onClick) {
            onClick(paymentData);
            return;
        }

        // HORO-10478: every purchase entry point — trial, subscription and
        // message bundle — now lands on the redesigned checkout. The legacy
        // `/payment` page stays routable for the retry link on the failure page
        // until it's retired.
        goToCheckout(paymentData);
    };

    return (
        <button onClick={onPaymentBtn} className={classNames(styles.btn, ...extraClasses, { [styles.disabled]: loading || disable })}>
            {loading ? <Spinner width={24} height={24} /> : !!text && text}
        </button>
    );
};

export default PaymentButton;
